import { useState } from 'react';
import { GoLink, PageBanner, Section, useDocument } from '@longnan/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage } from '../useSiteData';
import {} from './common';
import type { ImageryDoc, NewsDoc, NewsItem } from '../types';

type Filter = 'all' | NewsItem['tag'];
const FILTERS: { key: Filter; labelKey: string }[] = [
  { key: 'all', labelKey: 'news.filter.all' },
  { key: 'group', labelKey: 'news.filter.group' },
  { key: 'subsidiary', labelKey: 'news.filter.subsidiary' },
  { key: 'esg', labelKey: 'news.filter.esg' },
];

function Loading({ error }: { error: string | null }) {
  const { t } = useT();
  return error ? (
    <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{error}</div>
  ) : (
    <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>
  );
}

function List({ items, imagery }: { items: NewsItem[]; imagery?: ImageryDoc | null }) {
  const { t, pick } = useT();
  const [filter, setFilter] = useState<Filter>('all');
  const shown = filter === 'all' ? items : items.filter((n) => n.tag === filter);
  const img = imagery ? findImage(imagery, 'convention') : null;
  return (
    <>
      <PageBanner
        image={img ? assetUrl(img.local) : undefined}
        imageAlt={img ? pick(img.alt) : undefined}
        imageCredit={img ? creditOf(img) : undefined}
        eyebrow={t('news.eyebrow')}
        title={t('news.title')}
        lead={t('news.lead')}
      />
      <Section id="list">
        <div role="group" aria-label={t('news.eyebrow')} className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                filter === f.key
                  ? 'border-primary bg-primary text-on-dark'
                  : 'border-line-strong text-body hover:border-primary hover:text-primary-light'
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
        {shown.length === 0 ? (
          <p className="mt-12 text-muted">{t('news.empty')}</p>
        ) : (
          <ul className="mt-10 divide-y divide-line border-y border-line">
            {shown.map((n) => (
              <li key={n.id} className="grid gap-4 py-8 md:grid-cols-[8rem_1fr]">
                <div className="tnum text-sm text-muted">{n.date}</div>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded bg-surface px-2 py-0.5 text-xs text-muted-light">{pick(n.category)}</span>
                  </div>
                  <h2 className="mt-3 text-xl font-semibold leading-snug text-body">
                    <GoLink to={`/news/${n.id}`} className="hover:text-primary-light hover:underline">
                      {pick(n.title)}
                    </GoLink>
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{pick(n.summary)}</p>
                  <GoLink to={`/news/${n.id}`} className="mt-4 inline-flex text-sm font-semibold text-primary-light hover:underline">
                    {t('news.readMore')} →
                  </GoLink>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

function Detail({ items, id }: { items: NewsItem[]; id: string }) {
  const { t, pick } = useT();
  const item = items.find((n) => n.id === id);
  if (!item) {
    return (
      <Section id="missing">
        <p className="text-lg text-body">{t('news.notFound')}</p>
        <GoLink to="/news" className="mt-6 inline-flex text-sm font-semibold text-primary-light hover:underline">
          ← {t('news.back')}
        </GoLink>
      </Section>
    );
  }
  const related = items.filter((n) => n.id !== item.id).slice(0, 2);
  return (
    <>
      <Section id="article">
        <GoLink to="/news" className="inline-flex text-sm font-semibold text-primary-light hover:underline">
          ← {t('news.back')}
        </GoLink>
        <article className="mx-auto mt-10 max-w-3xl">
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted">
            <span className="tnum">{item.date}</span>
            <span className="rounded bg-surface px-2 py-0.5 text-xs text-muted-light">{pick(item.category)}</span>
          </div>
          <h1 className="mt-5 text-3xl font-semibold leading-tight text-body md:text-[2.5rem]">{pick(item.title)}</h1>
          <p className="mt-6 text-lg leading-relaxed text-muted">{pick(item.summary)}</p>
          <div className="mt-10 space-y-6 text-base leading-relaxed text-body md:text-lg">
            {item.body.map((p, i) => (
              <p key={i}>{pick(p)}</p>
            ))}
          </div>
        </article>
      </Section>
      {related.length > 0 && (
        <Section id="related" tone="surface">
          <h2 className="text-xl font-semibold text-body">{t('news.related')}</h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {related.map((n) => (
              <li key={n.id} className="rounded-xl border border-line bg-bg p-6">
                <div className="tnum text-xs text-muted">{n.date}</div>
                <h3 className="mt-2 text-base font-semibold text-body">
                  <GoLink to={`/news/${n.id}`} className="hover:text-primary-light hover:underline">
                    {pick(n.title)}
                  </GoLink>
                </h3>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

export function News({ id }: { id?: string }) {
  const doc = useDocument<NewsDoc>('group', 'news.json');
  const imgDoc = useDocument<ImageryDoc>('group', 'imagery.json');
  if (doc.error) return <Loading error={doc.error} />;
  if (!doc.data) return <Loading error={null} />;
  return id ? <Detail items={doc.data.items} id={id} /> : <List items={doc.data.items} imagery={imgDoc.data} />;
}
